'use client';

import { useActionState } from 'react';
import {
    createContactUs,
    type ContactUsFormValues,
    type ContactUsState,
} from '@/lib/services/actions/';
import Logo from '@/components/ui/navigation/top-menu/logo';
import CloseMenuIconButton from '@/components/ui/common/close-button';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import useContactUsContext from '@/components/providers/context/contact-us-context';
import Image from 'next/image';
import CustomLink from '@/components/ui/common/custom-link';
import { useLocale } from '@/components/providers/context/locale-context';
import SubmitButton from './submit-button';
import {
    buildStaticAssetPath,
    getStaticAssetUrl,
} from '@/app/lib/utils/static-asset-url';

const EMPTY_CONTACT_FORM_VALUES: ContactUsFormValues = {
    name: '',
    email: '',
    occupation: '',
    phone: '',
    wechat: '',
    message: '',
    submittedAt: '',
};

const INITIAL_CONTACT_FORM_STATE: ContactUsState = {
    fields: EMPTY_CONTACT_FORM_VALUES,
    message: '',
    errors: {},
    isSubmitted: false,
};

export default function ContactUsForm() {
    const { t } = useTranslation('contact-us-form');
    const locale = useLocale();
    const [state, dispatch] = useActionState(
        createContactUs,
        INITIAL_CONTACT_FORM_STATE,
    );
    const { getFormStatus, closeForm } = useContactUsContext();
    const isFormOpen = getFormStatus();
    const [scrolled, setScrolled] = useState(false);
    const mobileContainerRef = useRef<HTMLDivElement>(null);
    const [formValues, setFormValues] = useState<ContactUsFormValues>(
        EMPTY_CONTACT_FORM_VALUES,
    );

    useEffect(() => {
        const handleScroll = () => {
            if (!mobileContainerRef.current) {
                return;
            }

            const scrolled = mobileContainerRef.current.scrollTop > 1;
            setScrolled(scrolled);
        };

        handleScroll();

        mobileContainerRef.current?.addEventListener('scroll', handleScroll);
        return () =>
            mobileContainerRef.current?.removeEventListener(
                'scroll',
                handleScroll,
            );
    }, [locale, mobileContainerRef.current]);

    useEffect(() => {
        if (state.isSubmitted) {
            setFormValues(EMPTY_CONTACT_FORM_VALUES);
            return;
        }

        if (state.fields) {
            setFormValues(state.fields);
        }
    }, [state.fields, state.isSubmitted]);

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        const { name, value } = e.target;
        setFormValues((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^1[3-9]\d{9}$/;
    const wechatRegex = /^[a-zA-Z_][a-zA-Z0-9_-]{5,19}$/;

    const isPhoneInvalid =
        locale === 'zh' &&
        formValues.phone.length > 0 &&
        !phoneRegex.test(formValues.phone);
    const isWechatInvalid =
        locale === 'zh' &&
        formValues.wechat.length > 0 &&
        !wechatRegex.test(formValues.wechat);

    const isFormValid =
        emailRegex.test(formValues.email) &&
        formValues.name.length >= 1 &&
        formValues.occupation.length >= 1 &&
        formValues.message.length >= 1 &&
        !isPhoneInvalid &&
        !isWechatInvalid;

    const isEmailInvalid =
        formValues.email.length > 0 && !emailRegex.test(formValues.email);
    const contactCoverBackgroundImageUrl = getStaticAssetUrl(
        buildStaticAssetPath('bg-contact-us-cover.webp'),
    );

    return (
        <div
            className={clsx(
                'fixed left-[50%] top-[50%] z-[9999] flex h-screen w-screen -translate-x-[50%] -translate-y-[50%] items-center justify-center bg-[#00000033] transition-opacity duration-200',
                {
                    'pointer-events-none invisible opacity-0': !isFormOpen,
                    'visible opacity-100': isFormOpen,
                },
            )}
        >
            {/* web端 */}
            <div
                className={clsx(
                    'relative mx-[40px] hidden h-[712px] w-[1240px] flex-row overflow-hidden rounded-[24px] transition-all duration-300 ease-in-out sm:flex',
                    {
                        'invisible scale-90 opacity-0': !isFormOpen,
                        'visible scale-100 opacity-100': isFormOpen,
                    },
                )}
            >
                <div
                    className="flex h-full basis-1/2 flex-col justify-between bg-cover bg-center bg-no-repeat px-[60px] pb-[64px] pt-[50px]"
                    style={{
                        backgroundImage: `url(${contactCoverBackgroundImageUrl})`,
                    }}
                >
                    <div className="flex w-full flex-col">
                        <div
                            className={clsx(
                                'j-h3 flex w-full flex-col !leading-[1.5] leading-[84px] text-white',
                            )}
                        >
                            <div>{t('cover.sayHi')}</div>
                            <div className="w-full whitespace-nowrap">
                                <span>
                                    {t('cover.content.firstLine.part1')}
                                </span>
                                <span
                                    style={{
                                        WebkitTextFillColor: 'transparent',
                                    }}
                                    className="bg-[linear-gradient(90deg,_#7857ff_8.37%,_#e7a9fd_56.02%)] bg-clip-text"
                                >
                                    {t('cover.content.firstLine.part2')}
                                </span>
                            </div>
                            <div>{t('cover.content.secondLine')}</div>
                        </div>
                        <div className={clsx('j-t1 mt-[16px] text-white')}>
                            {t('cover.response')}
                        </div>
                    </div>
                    <div className="flex flex-col">
                        <Logo color="#FFFFFF" width={81} height={28} />
                        <div className={clsx('j-t2 text-[#C8C8C8]')}>
                            {t('cover.slogan')}
                        </div>
                    </div>
                </div>

                <div className={clsx('h-full basis-1/2 bg-white', {})}>
                    <form
                        action={dispatch}
                        className={clsx('h-full w-full', {
                            hidden: state.isSubmitted,
                            ' block': !state.isSubmitted,
                        })}
                    >
                        <div
                            className={clsx('j-t2 h-full px-[60px] pt-[60px]')}
                        >
                            <div className="w-full">
                                {/* name */}
                                <div>
                                    <label
                                        className="text-[#515151] after:ml-0.5 after:text-red-500 after:content-['*'] "
                                        htmlFor="name"
                                    >
                                        {t('form.name.label')}
                                    </label>
                                    <input
                                        className="mb-[24px] mt-[8px] block w-full rounded-[100px] border border-gray-200 py-[9px] pl-3 outline-2 placeholder:text-[#878789]"
                                        id="name"
                                        type="text"
                                        name="name"
                                        value={formValues.name}
                                        placeholder={t('form.name.placeholder')}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>
                                {/* occupation/profession */}
                                <div>
                                    <label
                                        className=" text-[#515151] after:ml-0.5 after:text-red-500 after:content-['*']"
                                        htmlFor="occupation"
                                    >
                                        {t('form.occupation.label')}
                                    </label>
                                    <input
                                        className="mb-[24px] mt-[8px] block w-full rounded-[100px] border border-gray-200 py-[9px] pl-3 outline-2 placeholder:text-gray-500"
                                        id="occupation"
                                        type="text"
                                        name="occupation"
                                        value={formValues.occupation}
                                        placeholder={t(
                                            'form.occupation.placeholder',
                                        )}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>
                                {/* email */}
                                <div className="relative mb-[24px]">
                                    <label
                                        className="text-[#515151] after:ml-0.5 after:text-red-500 after:content-['*']"
                                        htmlFor="email"
                                    >
                                        {t('form.email.label')}
                                    </label>
                                    <input
                                        className={clsx(
                                            'mt-[8px] block w-full rounded-[100px] border py-[9px] pl-3 outline-2 placeholder:text-gray-500',
                                            isEmailInvalid
                                                ? 'border-red-500 focus:border-red-500 focus:outline-red-500'
                                                : 'border-gray-200',
                                        )}
                                        id="email"
                                        type="email"
                                        name="email"
                                        value={formValues.email}
                                        placeholder={t(
                                            'form.email.placeholder',
                                        )}
                                        onChange={handleChange}
                                        required
                                    />
                                    {isEmailInvalid && (
                                        <p className="absolute -bottom-[20px] left-[12px] text-[12px] text-red-500">
                                            {t('form.email.error')}
                                        </p>
                                    )}
                                </div>
                                {/* phone + wechat (仅中文版) */}
                                {locale === 'zh' && (
                                    <div className="mb-[8px] flex gap-[12px]">
                                        <div className="relative flex-1 pb-[20px]">
                                            <label
                                                className="text-[#515151]"
                                                htmlFor="phone"
                                            >
                                                {t('form.phone.label')}
                                            </label>
                                            <input
                                                className={clsx(
                                                    'mt-[8px] block w-full rounded-[100px] border py-[9px] pl-3 outline-2 placeholder:text-gray-500',
                                                    isPhoneInvalid
                                                        ? 'border-red-500'
                                                        : 'border-gray-200',
                                                )}
                                                id="phone"
                                                type="tel"
                                                name="phone"
                                                value={formValues.phone}
                                                placeholder={t(
                                                    'form.phone.placeholder',
                                                )}
                                                onChange={handleChange}
                                            />
                                            {isPhoneInvalid && (
                                                <p className="absolute bottom-[2px] left-[12px] text-[11px] leading-tight text-red-500">
                                                    {t('form.phone.error')}
                                                </p>
                                            )}
                                        </div>
                                        <div className="relative flex-1 pb-[20px]">
                                            <label
                                                className="text-[#515151]"
                                                htmlFor="wechat"
                                            >
                                                {t('form.wechat.label')}
                                            </label>
                                            <input
                                                className={clsx(
                                                    'mt-[8px] block w-full rounded-[100px] border py-[9px] pl-3 outline-2 placeholder:text-gray-500',
                                                    isWechatInvalid
                                                        ? 'border-red-500'
                                                        : 'border-gray-200',
                                                )}
                                                id="wechat"
                                                type="text"
                                                name="wechat"
                                                value={formValues.wechat}
                                                placeholder={t(
                                                    'form.wechat.placeholder',
                                                )}
                                                onChange={handleChange}
                                            />
                                            {isWechatInvalid && (
                                                <p className="absolute bottom-[2px] left-[12px] text-[11px] leading-tight text-red-500">
                                                    {t('form.wechat.error')}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                )}
                                {/* message to us */}
                                <div>
                                    <label
                                        className="text-[#515151] after:ml-0.5 after:text-red-500 after:content-['*']"
                                        htmlFor="message"
                                    >
                                        {t('form.message.label')}
                                    </label>
                                    <textarea
                                        className="mt-[8px] block w-full resize-none rounded-[24px] border border-gray-200 py-3 pl-3 outline-2 placeholder:text-gray-500"
                                        id="message"
                                        name="message"
                                        value={formValues.message}
                                        placeholder={t(
                                            'form.message.placeholder',
                                        )}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>
                            </div>
                            <div className="mt-[36px] flex h-[48px] w-full flex-row items-center">
                                <div className={clsx('j-p1 h-full basis-3/4')}>
                                    <div>{t('form.sendEmail')}</div>
                                    <CustomLink
                                        href={
                                            locale === 'en'
                                                ? 'mailto:contact@join-intelligence.com?subject=Contact%20Us&body=Hi%20JoinAI%20Team,%0D%0A%0D%0AI%20am%20contacting%20you%20regarding%20%5Bbriefly%20state%20the%20reason,%20e.g.,%20product%20inquiry,%20partnership%20opportunity,%20etc.%5D.%20%0D%0A%0D%0A%5BProvide%20a%20short%20introduction%20about%20yourself%20or%20your%20company%5D%0D%0A%0D%0A%5BDetail%20your%20inquiry%20or%20request%5D%0D%0A%0D%0AThank%20you%20for%20your%20time%20and%20assistance.%0D%0A%0D%0ABest%20regards,%0D%0A%5BYour%20Name%5D%0D%0A%5BYour%20Contact%20Information,%20if%20necessary%5D'
                                                : 'mailto:contact@join-intelligence.com?subject=%E8%81%94%E7%B3%BB%E6%88%91%E4%BB%AC&body=%E6%82%A8%E5%A5%BD%20%E5%8D%93%E5%8D%B0%E6%99%BA%E8%83%BD%20%E5%9B%A2%E9%98%9F%EF%BC%8C%0D%0A%0D%0A%E6%88%91%E5%9B%A0%5B%E7%AE%80%E8%BF%B0%E7%90%86%E7%94%B1%EF%BC%8C%E5%A6%82%EF%BC%9A%E4%BA%A7%E5%93%81%E5%92%A8%E8%AF%A2%E3%80%81%E5%90%88%E4%BD%9C%E6%9C%BA%E4%BC%9A%E7%AD%89%5D%E8%80%8C%E8%81%94%E7%B3%BB%E5%88%B0%E6%82%A8%E3%80%82%0D%0A%0D%0A%5B%E7%AE%80%E5%8D%95%E4%BB%8B%E7%BB%8D%E8%87%AA%E5%B7%B1%E6%88%96%E6%82%A8%E7%9A%84%E5%85%AC%E5%8F%B8%5D%0D%0A%0D%0A%5B%E8%AF%A6%E7%BB%86%E9%98%90%E8%BF%B0%E6%82%A8%E7%9A%84%E5%92%A8%E8%AF%A2%E6%88%96%E8%AF%B7%E6%B1%82%5D%0D%0A%0D%0A%E6%84%9F%E8%B0%A2%E6%82%A8%E7%9A%84%E6%97%B6%E9%97%B4%E5%92%8C%E5%B8%AE%E5%8A%A9%E3%80%82%0D%0A%0D%0A%E6%AD%A4%E8%87%B4%EF%BC%8C%0D%0A%5B%E6%82%A8%E7%9A%84%E5%A7%93%E5%90%8D%5D%0D%0A%5B%E5%A6%82%E6%9C%89%E5%BF%85%E8%A6%81%EF%BC%8C%E9%99%84%E4%B8%8A%E6%82%A8%E7%9A%84%E8%81%94%E7%B3%BB%E4%BF%A1%E6%81%AF%5D'
                                        }
                                        iconSize={16}
                                    >
                                        contact@join-intelligence.com
                                    </CustomLink>
                                </div>
                                <div className="h-full basis-1/4">
                                    <SubmitButton
                                        title={t('form.submit')}
                                        locale={locale}
                                        disabled={!isFormValid}
                                    />
                                </div>
                            </div>

                            {/* <div
                                className="flex h-8 items-end space-x-1"
                                aria-live="polite"
                                aria-atomic="true"
                            >
                                {state.errors && (
                                    <>
                                        <ExclamationCircleIcon className="h-5 w-5 text-red-500" />
                                        <p className="text-sm text-red-500">
                                            {state.message}
                                        </p>
                                    </>
                                )}
                            </div> */}
                        </div>
                    </form>
                    <div
                        className={clsx(
                            'flex h-full w-full flex-row items-center justify-center',
                            {
                                hidden: !state.isSubmitted,
                                ' block': state.isSubmitted,
                            },
                        )}
                    >
                        <div className="flex w-full flex-col items-center justify-center">
                            <div className="w-[200px]">
                                <Image
                                    alt="icon-contact-us-form-submit-success.webp"
                                    src={getStaticAssetUrl(
                                        '/icon-contact-us-form-submit-success.webp',
                                    )}
                                    sizes="100vw"
                                    style={{
                                        width: '100%',
                                        height: 'auto',
                                    }}
                                    width={400}
                                    height={336}
                                />
                            </div>

                            <div
                                className={clsx(
                                    'j-h5 mt-[24px] text-[#06060A]',
                                )}
                            >
                                {t('form.submitSuccess')}
                            </div>
                            <div
                                className={clsx(
                                    'j-t2 mt-[12px] w-3/5 text-center text-[##515151]',
                                )}
                            >
                                {t('form.submitInfo')}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="absolute right-[28px] top-[28px]">
                    <CloseMenuIconButton
                        fill="#000000"
                        onClose={closeForm}
                        width={24}
                        height={24}
                    />
                </div>
            </div>

            {/* 移动端 */}
            <div
                ref={mobileContainerRef}
                className="relative flex h-full w-full flex-col overflow-auto sm:hidden"
            >
                <div
                    className="flex max-h-[375px] min-h-[375px] w-full flex-1 flex-col justify-between bg-cover bg-center bg-no-repeat px-[20px] py-[24px]"
                    style={{
                        backgroundImage: `url(${contactCoverBackgroundImageUrl})`,
                    }}
                >
                    <div className="flex h-full w-full flex-col justify-end">
                        <div
                            className={clsx(
                                'j-h3 flex w-full flex-col leading-[40px] text-white',
                            )}
                        >
                            <div className="w-full whitespace-nowrap">
                                {t('cover.sayHi')}
                            </div>
                            <div className="w-full whitespace-nowrap">
                                <span>
                                    {t('cover.content.firstLine.part1')}
                                </span>
                                <span
                                    style={{
                                        WebkitTextFillColor: 'transparent',
                                    }}
                                    className="bg-[linear-gradient(90deg,_#7857ff_8.37%,_#e7a9fd_56.02%)] bg-clip-text"
                                >
                                    {t('cover.content.firstLine.part2')}
                                </span>
                            </div>
                            <div>{t('cover.content.secondLine')}</div>
                        </div>
                        <div className={clsx('j-t2 mt-[6px] text-white')}>
                            {t('cover.response')}
                        </div>
                    </div>
                </div>
                <form
                    action={dispatch}
                    className={clsx('w-full shrink-0', {
                        hidden: state.isSubmitted,
                    })}
                >
                    <div
                        className={clsx(
                            'j-t2 h-full bg-white px-[20px] pt-[24px]',
                        )}
                    >
                        <div className="w-full">
                            {/* name */}
                            <div>
                                <label
                                    className="ml-[6px] text-[#515151] after:ml-0.5 after:text-red-500 after:content-['*']"
                                    htmlFor="name"
                                >
                                    {t('form.name.label')}
                                </label>
                                <input
                                    className="mb-[24px] mt-[8px] block w-full rounded-[100px] border border-gray-200 py-[9px] pl-3 outline-2 placeholder:text-[#878789]"
                                    id="name"
                                    type="text"
                                    name="name"
                                    value={formValues.name}
                                    placeholder={t('form.name.placeholder')}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                            {/* occupation/profession */}
                            <div>
                                <label
                                    className=" ml-[6px] text-[#515151] after:ml-0.5 after:text-red-500 after:content-['*']"
                                    htmlFor="occupation"
                                >
                                    {t('form.occupation.label')}
                                </label>
                                <input
                                    className="mb-[24px] mt-[8px] block w-full rounded-[100px] border border-gray-200 py-[9px] pl-3 outline-2 placeholder:text-gray-500"
                                    id="occupation"
                                    type="text"
                                    name="occupation"
                                    value={formValues.occupation}
                                    placeholder={t(
                                        'form.occupation.placeholder',
                                    )}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                            {/* email */}
                            <div className="relative mb-[24px]">
                                <label
                                    className="ml-[6px] text-[#515151] after:ml-0.5 after:text-red-500 after:content-['*']"
                                    htmlFor="email"
                                >
                                    {t('form.email.label')}
                                </label>
                                <input
                                    className={clsx(
                                        'mt-[8px] block w-full rounded-[100px] border py-[9px] pl-3 outline-2 placeholder:text-gray-500',
                                        isEmailInvalid
                                            ? 'border-red-500 focus:border-red-500 focus:outline-red-500'
                                            : 'border-gray-200',
                                    )}
                                    id="email"
                                    type="email"
                                    name="email"
                                    value={formValues.email}
                                    placeholder={t('form.email.placeholder')}
                                    onChange={handleChange}
                                    required
                                />
                                {isEmailInvalid && (
                                    <p className="absolute -bottom-[20px] left-[12px] text-[12px] text-red-500">
                                        {t('form.email.error')}
                                    </p>
                                )}
                            </div>
                            {/* phone + wechat (仅中文版) */}
                            {locale === 'zh' && (
                                <div className="mb-[24px] flex flex-col gap-[8px]">
                                    <div className="relative pb-[20px]">
                                        <label
                                            className="ml-[6px] text-[#515151]"
                                            htmlFor="phone-mobile"
                                        >
                                            {t('form.phone.label')}
                                        </label>
                                        <input
                                            className={clsx(
                                                'mt-[8px] block w-full rounded-[100px] border py-[9px] pl-3 outline-2 placeholder:text-gray-500',
                                                isPhoneInvalid
                                                    ? 'border-red-500'
                                                    : 'border-gray-200',
                                            )}
                                            id="phone-mobile"
                                            type="tel"
                                            name="phone"
                                            value={formValues.phone}
                                            placeholder={t(
                                                'form.phone.placeholder',
                                            )}
                                            onChange={handleChange}
                                        />
                                        {isPhoneInvalid && (
                                            <p className="absolute bottom-[2px] left-[18px] text-[11px] leading-tight text-red-500">
                                                {t('form.phone.error')}
                                            </p>
                                        )}
                                    </div>
                                    <div className="relative pb-[20px]">
                                        <label
                                            className="ml-[6px] text-[#515151]"
                                            htmlFor="wechat-mobile"
                                        >
                                            {t('form.wechat.label')}
                                        </label>
                                        <input
                                            className={clsx(
                                                'mt-[8px] block w-full rounded-[100px] border py-[9px] pl-3 outline-2 placeholder:text-gray-500',
                                                isWechatInvalid
                                                    ? 'border-red-500'
                                                    : 'border-gray-200',
                                            )}
                                            id="wechat-mobile"
                                            type="text"
                                            name="wechat"
                                            value={formValues.wechat}
                                            placeholder={t(
                                                'form.wechat.placeholder',
                                            )}
                                            onChange={handleChange}
                                        />
                                        {isWechatInvalid && (
                                            <p className="absolute bottom-[20px] left-[18px] text-[11px] leading-tight text-red-500">
                                                {t('form.wechat.error')}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            )}
                            {/* message to us */}
                            <div>
                                <label
                                    className="ml-[6px] text-[#515151] after:ml-0.5 after:text-red-500 after:content-['*']"
                                    htmlFor="message"
                                >
                                    {t('form.message.label')}
                                </label>
                                <textarea
                                    className="mt-[8px] block w-full resize-none rounded-[24px] border border-gray-200 py-3 pl-3 outline-2 placeholder:text-gray-500"
                                    id="message"
                                    name="message"
                                    value={formValues.message}
                                    placeholder={t('form.message.placeholder')}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                        </div>
                        <div className="mt-[24px] flex w-full flex-col items-center gap-[24px] pb-[34px]">
                            <div className="h-[40px] w-full">
                                <SubmitButton
                                    title={t('form.submit')}
                                    locale={locale}
                                    disabled={!isFormValid}
                                />
                            </div>

                            <div className={clsx('j-p1 w-full')}>
                                <div>{t('form.sendEmail')}</div>
                                <CustomLink
                                    href={
                                        locale === 'en'
                                            ? 'mailto:contact@join-intelligence.com?subject=Contact%20Us&body=Hi%20JoinAI%20Team,%0D%0A%0D%0AI%20am%20contacting%20you%20regarding%20%5Bbriefly%20state%20the%20reason,%20e.g.,%20product%20inquiry,%20partnership%20opportunity,%20etc.%5D.%20%0D%0A%0D%0A%5BProvide%20a%20short%20introduction%20about%20yourself%20or%20your%20company%5D%0D%0A%0D%0A%5BDetail%20your%20inquiry%20or%20request%5D%0D%0A%0D%0AThank%20you%20for%20your%20time%20and%20assistance.%0D%0A%0D%0ABest%20regards,%0D%0A%5BYour%20Name%5D%0D%0A%5BYour%20Contact%20Information,%20if%20necessary%5D'
                                            : 'mailto:contact@join-intelligence.com?subject=%E8%81%94%E7%B3%BB%E6%88%91%E4%BB%AC&body=%E6%82%A8%E5%A5%BD%20%E5%8D%93%E5%8D%B0%E6%99%BA%E8%83%BD%20%E5%9B%A2%E9%98%9F%EF%BC%8C%0D%0A%0D%0A%E6%88%91%E5%9B%A0%5B%E7%AE%80%E8%BF%B0%E7%90%86%E7%94%B1%EF%BC%8C%E5%A6%82%EF%BC%9A%E4%BA%A7%E5%93%81%E5%92%A8%E8%AF%A2%E3%80%81%E5%90%88%E4%BD%9C%E6%9C%BA%E4%BC%9A%E7%AD%89%5D%E8%80%8C%E8%81%94%E7%B3%BB%E5%88%B0%E6%82%A8%E3%80%82%0D%0A%0D%0A%5B%E7%AE%80%E5%8D%95%E4%BB%8B%E7%BB%8D%E8%87%AA%E5%B7%B1%E6%88%96%E6%82%A8%E7%9A%84%E5%85%AC%E5%8F%B8%5D%0D%0A%0D%0A%5B%E8%AF%A6%E7%BB%86%E9%98%90%E8%BF%B0%E6%82%A8%E7%9A%84%E5%92%A8%E8%AF%A2%E6%88%96%E8%AF%B7%E6%B1%82%5D%0D%0A%0D%0A%E6%84%9F%E8%B0%A2%E6%82%A8%E7%9A%84%E6%97%B6%E9%97%B4%E5%92%8C%E5%B8%AE%E5%8A%A9%E3%80%82%0D%0A%0D%0A%E6%AD%A4%E8%87%B4%EF%BC%8C%0D%0A%5B%E6%82%A8%E7%9A%84%E5%A7%93%E5%90%8D%5D%0D%0A%5B%E5%A6%82%E6%9C%89%E5%BF%85%E8%A6%81%EF%BC%8C%E9%99%84%E4%B8%8A%E6%82%A8%E7%9A%84%E8%81%94%E7%B3%BB%E4%BF%A1%E6%81%AF%5D'
                                    }
                                    iconSize={16}
                                >
                                    contact@join-intelligence.com
                                </CustomLink>
                            </div>
                        </div>

                        {/* <div
                            className="flex items-end space-x-1"
                            aria-live="polite"
                            aria-atomic="true"
                        >
                            {state.errors && (
                                <>
                                    <ExclamationCircleIcon className="h-5 w-5 text-red-500" />
                                    <p className="text-sm text-red-500">
                                        {state.message}
                                    </p>
                                </>
                            )}
                        </div> */}
                    </div>
                </form>

                <div
                    className={clsx(
                        'flex min-h-[calc(100vh-375px)] w-full shrink-0 items-center justify-center bg-white px-[20px] py-[48px]',
                        {
                            hidden: !state.isSubmitted,
                        },
                    )}
                >
                    <div className="flex w-full flex-col items-center justify-center">
                        <div className="w-[160px]">
                            <Image
                                alt="icon-contact-us-form-submit-success.webp"
                                src={getStaticAssetUrl(
                                    '/icon-contact-us-form-submit-success.webp',
                                )}
                                sizes="160px"
                                style={{
                                    width: '100%',
                                    height: 'auto',
                                }}
                                width={400}
                                height={336}
                            />
                        </div>
                        <div className="j-h5 mt-[24px] text-[#06060A]">
                            {t('form.submitSuccess')}
                        </div>
                        <div className="j-t2 mt-[12px] text-center text-[#515151]">
                            {t('form.submitInfo')}
                        </div>
                    </div>
                </div>

                <div
                    className={clsx(
                        'fixed left-0 right-0 top-0 flex h-[56px] items-center justify-center px-[20px] [border-bottom:0.5px_solid_transparent]',
                        {
                            'bg-[#ffffffcc] backdrop-blur-[32px] backdrop-filter [border-bottom:0.5px_solid_#dfdfdf]':
                                scrolled,
                            'bg-transparent': !scrolled,
                        },
                    )}
                >
                    <div className="flex h-full w-full flex-row items-center justify-between">
                        {scrolled ? (
                            <Logo height={28} width={130} />
                        ) : (
                            <Logo color="#FFFFFF" height={28} width={130} />
                        )}
                        <CloseMenuIconButton
                            fill="#FFFFFF"
                            onClose={closeForm}
                            width={28}
                            height={28}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}
